import React, { useEffect, useMemo } from 'react';
import { Form, Formik }     from 'formik';
import { compose }          from 'redux';
import { connect }          from 'react-redux';
import {
  POI_TYPE, LOCALES, USER_ROLES,
} from '@bagonboard/wiseman';
import SweetAlert from 'react-bootstrap-sweetalert';
import {
  Button, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, CircularProgress,
} from '@material-ui/core';
// @material-ui/icons
import {
  Delete as DeleteIcon,
  Close as CloseIcon,
} from '@material-ui/icons';

import PropTypes from 'prop-types';
import { useSnackbar } from 'hooks';
import { UserInfo } from './components/index';
import { ConfirmButton } from '#components';

import {
  useCompanies, usePois, useUser, useAreas, useNewUser,
} from '#views/Dashboard/hooks';
import * as usersActions  from '../../../../../reducers/Users/actions';

import { validationSchema, validateCompany } from './schemas';
import { useStyles } from './styles';
import { checkHooksAreReady } from './utils';

const makeInitialValues = user => ({
  // eslint-disable-next-line no-underscore-dangle
  id      : user._id,
  name    : user.name || '',
  surname : user.surname || '',
  phone   : user.phone || '',
  email   : user.email || '',
  language: user.language || LOCALES.es,
  role    : user.role || '',
  areas   : user.areas || [],
  poi     : user.poi || '',
  terminal: user.terminal || '',
  company : user.company || '',
  password: '',
  // userReferrer: '' => TODO on back => add user id who creates the new user.
});

const ADMINS = [USER_ROLES.ADMIN, USER_ROLES.SUPER_ADMIN];

const UserDialog = ({
  onClose,
  open,
  user: selectedUser,
  usersFetch,
}) => {
  const { data: { role: currentRole, id: currentUserId } } = useUser();
  const { enqueueSnackbar } = useSnackbar();
  const { pois, handlers: poisHandlers } = usePois();
  // const { areas, handlers: areasHandlers } = useAreas();
  const { handlers: userHandlers } = useNewUser();
  const classes = useStyles();
  // eslint-disable-next-line no-underscore-dangle
  const isUpdate = Boolean(selectedUser._id);
  const initialValues = makeInitialValues(selectedUser);
  const isSameUser = useMemo(() => (currentUserId === selectedUser._id),
    [currentUserId, selectedUser._id]);

  useEffect(
    () => {
      poisHandlers.fetch(
        { find: { type: POI_TYPE.AIRPORT } },
        { enableSnackbar: true }
      );
    },
    [poisHandlers.fetch]
  );

  const checkDisabled = ({ role }) => isUpdate
    && role === currentRole
    && !ADMINS.includes(currentRole)
    && !isSameUser;

  // TODO: Cannot use useAreas hooks until refactor all the old areas store
  // and calls in old class views
  // useEffect(
  //   () => {
  //     areasHandlers.fetch(
  //       { find: {} },
  //       { enableSnackbar: true }
  //     );
  //   },
  //   [areasHandlers.fetch]
  // );
  const refreshAndClose = () => {
    usersFetch({});
    onClose();
  };
  const onCreate = async (params) => {
    try {
      await userHandlers.create(params);
      refreshAndClose();
    // TODO: CALL userFetch() function refactored
    } catch (notificationError) {
      const properProps = Array.isArray(notificationError)
        ? notificationError
        : [
          'Processing error',
          { variant: 'error' },
        ];
      enqueueSnackbar(...properProps);
    }
  };

  const onUpdate = async (userId, params) => {
    try {
      await userHandlers.update(userId, params);
      refreshAndClose();
    // TODO: CALL userFetch() function refactored
    } catch (notificationError) {
      const properProps = Array.isArray(notificationError)
        ? notificationError
        : [
          'Processing error',
          { variant: 'error' },
        ];
      enqueueSnackbar(...properProps);
    }
  };

  const onDelete = async (userId, params) => {
    try {
      await userHandlers.remove(userId, params);
      refreshAndClose();
    // TODO: CALL userFetch() function refactored
    } catch (notificationError) {
      const properProps = Array.isArray(notificationError)
        ? notificationError
        : [
          'Processing error',
          { variant: 'error' },
        ];
      enqueueSnackbar(...properProps);
    }
  };

  const onSubmitFormik = (values, { setSubmitting, submitForm }) => {
    const { id, ...body } = values;
    if (id) {
      onUpdate(id, { values: body, enableSnackbar: true, successSnackbar: 'User updated successfully' });
    } else {
      onCreate({ values: body, enableSnackbar: true, successSnackbar: 'User created successfully' });
    }
    // TODO: CALL userFetch() function refactored or added to hooks in some way.
  };

  // TODO: add the area when refactor the old store
  if (!checkHooksAreReady([pois].filter(Boolean))) {
    return (
      <SweetAlert
        confirmBtnStyle={{ display: 'none' }}
        show={[pois].some(({ loading }) => loading)}
        title=""
        onConfirm={() => {}}
      >
        <CircularProgress />
      </SweetAlert>
    );
  }

  return (
    <Dialog
      disableEnforceFocus
      onClose={onClose}
      classes={{
        paper: classes.root,
      }}
      open={open}
    >
      <div className={classes.titleContainer}>
        <DialogTitle>User details</DialogTitle>
        <IconButton color="secondary" aria-label="Close" size="large" onClick={onClose}>
          <CloseIcon fontSize="inherit" />
        </IconButton>
      </div>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={onSubmitFormik}
        validate={validateCompany(currentRole)}
      >
        {({
          values, setFieldValue,
        }) => {
          const isDisabled = checkDisabled(values);
          return (
            <Form className={classes.form}>
              <DialogContent className={classes.content}>
                <UserInfo values={values} isDisabled={isDisabled} setFieldValue={setFieldValue} />
              </DialogContent>
              <DialogActions className={classes.actions}>
                <Button
                  data-testid="btn.update-user-popup"
                  variant="contained"
                  color="primary"
                  type="submit"
                  disabled={isDisabled}
                >
                  { isUpdate ? 'Update' : 'Create' }
                </Button>
                {
                isUpdate && (
                  <ConfirmButton
                    data-testid="btn.delete-user"
                    onClick={() => onDelete(selectedUser._id, { values, enableSnackbar: true, successSnackbar: 'User removed successfully' })}
                    className={classes.deleteButton}
                    startIcon={<DeleteIcon />}
                    disabled={isDisabled || isSameUser}
                  >
                    Delete
                  </ConfirmButton>
                )
              }
              </DialogActions>
            </Form>
          );
        }}
      </Formik>
    </Dialog>
  );
};

UserDialog.propTypes = {
  usersFetch: PropTypes.func.isRequired,
  onClose   : PropTypes.func.isRequired,
  open      : PropTypes.bool.isRequired,
  user      : PropTypes.shape({}),
};

UserDialog.defaultProps = {
  user: {},
};

// TODO: Remove redux connect when refactor user hooks
const mapStateToProps = () => ({});

const mapDispatchToProps = dispatch => ({
  usersFetch: params => dispatch(usersActions.usersFetch(params)),
});

export default compose(
  connect(
    mapStateToProps,
    mapDispatchToProps,
  ),
)(UserDialog);
